import { Environment } from "../utils/Environment";
import React, { useState, useEffect } from "react";
import { postWithUserTableParams } from "../services/fetchWrapper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SuccessMessageComponent from "./SuccessMessageComponent";

const apiUrl = Environment.getApiUrl();

interface ProcessCostCalcRequest {
    job_id: string;
    client_id: string;
    base_table_name: string;
    pipe_material_cost_table: string;
    tbl_cost_table_name: string;
    large_pipe_tbl_cost_table_name: string;
    replace_score_col: string;
    total_cof_col: string;
    total_cofbin_col: string;
    pipe_diam: string;
    pipetype_col: string;
    pipe_length_col: string;
    critical_c_col: string;
    asset_id_col?: string;
}

const PerformCostCalculation: React.FC = () => {
    const [costCalcJson, setCostCalcJson] = useState<any | null>(null);
    const [mainUserJson, setMainUserJson] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [outputPath, setOutputPath] = useState("output.gpkg");

    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [processingDone, setProcessingDone] = useState(false);

    useEffect(() => {
        const fetchClientData = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            setLoading(true);
            setError(null);
            try {
                const response = await postWithUserTableParams(`${apiUrl}/get-client-data-for-cost-scoring`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch client data");
                }
                const data = await response.json();
                const costCalcJson = data.json;
                const mainUserJson = data.base_table;
                if (data.status !== 'success' || typeof data.json !== 'object' || data.json === null) {
                    throw new Error("Invalid response format from backend");
                }
                setCostCalcJson(costCalcJson);
                setMainUserJson(mainUserJson);
            } catch (err: any) {
                setError(err.message || "Failed to fetch client data");
            } finally {
                setLoading(false);
            }
        };
        fetchClientData();
    }, [apiUrl]);

    // Download geopackage with user-specified output path
    const handleDownloadGeopackage = async () => {
        // Get values from localStorage at function run time
        const username = localStorage.getItem("userName") || "";
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        try {
            setLoading(true);
            setError(null);
            // Ensure .gpkg extension
            let filename = outputPath.trim();
            if (!filename.toLowerCase().endsWith(".gpkg")) {
                filename += ".gpkg";
            }
            const response = await fetch(`${apiUrl}/download-geopackage`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    username,
                    client_id,
                    job_id,
                    package_save_path: filename
                }),
            });
            if (!response.ok) {
                throw new Error("Failed to download geopackage");
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            setError(err.message || "Failed to download geopackage");
        } finally {
            setLoading(false);
        }
    };

    // Process cost scores
    const handleProcessCostCalc = async () => {
        // Get values from localStorage at function run time
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        setLoading(true);
        setError(null);
        setShowSuccess(false);
        setProcessingDone(false);
        try {
            if (!costCalcJson) {
                throw new Error("No cost score JSON to process");
            }
            const request: ProcessCostCalcRequest = {
                job_id: job_id,
                client_id: client_id,
                base_table_name: mainUserJson.main_table_name,
                pipe_material_cost_table: "pipe_material_cost_table",
                tbl_cost_table_name: "cof_tbl_lkp",
                large_pipe_tbl_cost_table_name: "cof_lrg_pipe_tbl_lkp",
                replace_score_col: "replacecost",
                total_cof_col: "total_cof",
                total_cofbin_col: "tot_cofbin",
                pipe_diam: costCalcJson.pipe_diam,
                pipetype_col: costCalcJson.pipetype_col,
                pipe_length_col: costCalcJson.pipe_length_col,
                critical_c_col: costCalcJson.critical_c_col,
                asset_id_col: costCalcJson.asset_id_col
            };
            const response = await fetch(`${apiUrl}/run-full-cost-calculation`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(request),
            });
            if (!response.ok) {
                throw new Error("Failed to process cost score");
            }
            setUploadStatus("completed");
            setStatusMessage("COF scores processed successfully!");
            setShowSuccess(true);
        } catch {
            setShowSuccess(false);
            setUploadStatus("failed");
            setStatusMessage("Reach out to Admin");
        } finally {
            setProcessingDone(true);
            setLoading(false);
        }
    };

    return (
        <div>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textAlign: "center", textTransform: "uppercase" }}>
                COF Scores
            </Typography>
            <svg
                width="292"
                height="6"
                viewBox="0 0 292 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: "block", margin: "0 auto 16px" }}
            >
                <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
            </svg>
            <Typography variant="body2"
                sx={{
                    mb: 4,
                    maxWidth: 420,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    textAlign: "center",
                    mx: "auto"
                }}
            >
                It may take a few minutes to run the calculations. When done, under “Output Geopackage Filename” you can name the output whatever you want to call it, and click “download geopackage”.
            </Typography>
            {showSuccess ? (
                <SuccessMessageComponent message="COF scores processed successfully!" />
            ) : (
                processingDone && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                        <HighlightOffIcon sx={{ fontSize: 40, color: "#828282" }} />
                        <Typography variant="body2" sx={{ color: "#828282" }}>
                            Scoring did not complete successfully.
                        </Typography>
                    </Box>
                )
            )}
            {error && <div style={{ color: "red" }}>{error}</div>}
            <Typography variant="body2" sx={{ color: "#555", textAlign: "center", transform: "translateX(-40px)" }}>
                Output Geopackage File Name
            </Typography>
            <div style={{ margin: "16px 0" }}>
                <TextField
                    value={outputPath}
                    onChange={e => setOutputPath(e.target.value)}
                    sx={{
                        mb: 1,
                        width: 300,
                        maxWidth: "100%",
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#F6FFE8",
                            height: 36,
                        },
                        "& .MuiOutlinedInput-input": {
                            padding: "8px 12px",
                        },
                    }}
                />
            </div>
            <Button
                variant="outlined"
                disabled={loading || uploadStatus !== "completed"}
                onClick={handleDownloadGeopackage}
                sx={{
                    display: "block",
                    mx: "auto",
                    mb: 4,
                    mt: 2,
                    color: "#0373B5",
                    borderColor: "#0373B5",
                    "&:hover": {
                        borderColor: "#025f95",
                        backgroundColor: "rgba(3, 115, 181, 0.08)",
                    },
                    "&.Mui-disabled": {
                        color: "rgba(0,0,0,0.26)",
                        borderColor: "rgba(0,0,0,0.12)",
                    },
                }}
            >
                Download Geopackage
            </Button>
            {uploadStatus && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        maxWidth: 500,
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6">Processing Status</Typography>
                    <Typography variant="body2">
                        Status: {uploadStatus}
                    </Typography>
                    <Typography variant="body2">
                        Message: {statusMessage}
                    </Typography>
                </Box>
            )}
            <Button
                variant="contained"
                color="primary"
                disabled={loading || !costCalcJson}
                onClick={handleProcessCostCalc}
                sx={{
                    display: "block",
                    mx: "auto",
                    mb: 4,
                    mt: 2,
                    backgroundColor: "#0373B5"
                }}
            >
                {loading ? "Processing..." : "process cof scores"}
            </Button>
        </div>
    );
};

export default PerformCostCalculation;